
































































































































import { ConfirmButton } from '@/app/components';
import { Variable } from '@/app/interfaces';
import { S } from '@/app/utilities';
import { InputParameter, Loop } from '@/modules/workflow-designer/types';
import { computed, defineComponent, PropType, ref, watch } from '@vue/composition-api';
import * as R from 'ramda';

export default defineComponent({
    name: 'ComplexParameter',
    props: {
        workflowId: { type: String, required: true },
        value: {
            type: Object,
        },
        name: {
            type: String,
            required: true,
        },
        rules: {
            type: Object,
            default: () => {
                return {};
            },
        },
        parameter: {
            type: Object,
            required: true,
        },
        parameters: {
            type: Object,
            required: true,
        },
        dataframes: {
            type: Object,
            default: () => {
                return {};
            },
        },
        loops: {
            type: Array as PropType<Loop[]>,
            default: () => [],
        },
        columnsPerTask: {
            type: Object,
        },
        complexParameterIncomingProcessor: {
            type: Function,
            default: (parameterValue: any) => {
                return parameterValue.value;
            },
        },
        complexParameterOutgoingProcessor: {
            type: Function,
            default: (parameterValue: any) => {
                return { value: parameterValue };
            },
        },
        strict: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        visible: {
            type: Boolean,
            default: true,
        },

        isOnPremise: {
            type: Boolean,
            default: false,
        },
        parametersDefinition: {
            type: Object,
            required: true,
        },
        availableVariables: { type: Object as PropType<Record<string, Variable>>, default: () => {} },
        assetStructureForTask: {
            type: Object,
            default: () => {},
        },
    },
    // Handling circular dependency
    components: {
        TaskParameterConfiguration: () => import('../TaskParameterConfiguration.vue'),
        ConfirmButton,
    },
    setup(props, { emit }) {
        const forceUpdate = ref<Date>(new Date());
        // Finds the component value based on incoming value and defaults
        const defaultValue = computed(() => (props.parameter.multiple ? [] : null));
        const findInitialValue = (incomingValue: any) => {
            const processedValue = (props.complexParameterIncomingProcessor as any)(incomingValue);
            let resultingValue = props.parameter.multiple ? [] : {};

            if (!processedValue) {
                // Case no existing value defined
                if (S.has('default', props.parameter.validation)) {
                    // Case default value defined in validation information
                    resultingValue = props.parameter.validation.default;
                } else {
                    // Case no default value defined in validation information then take default empty value
                    resultingValue = props.parameter.multiple ? [] : {};
                }
            } else if (processedValue && props.parameter.multiple) {
                // Case where their is an existing value and the parameter is taking multiple values
                resultingValue = R.is(Array, processedValue) ? processedValue : [processedValue];
            } else if (processedValue && !props.parameter.multiple) {
                // Case where their is an existing value and the parameter is taking a single value
                resultingValue = processedValue;
            }
            return resultingValue;
        };

        const currentValue = ref<any>(findInitialValue(props.value));

        const isArray = computed(() => R.is(Array, currentValue.value));

        const hasValue = computed(() => !R.isNil(currentValue.value) && !R.isEmpty(currentValue.value));

        const subParameters = computed(() =>
            props.parameter.parameters.reduce((acc: any, parameter: any) => {
                acc[parameter.name] = parameter;
                return acc;
            }, {}),
        );

        const change = (parameter: InputParameter, settings: any, index: number | null = null) => {
            if (!currentValue.value || (R.is(Array, currentValue.value) && !currentValue.value.length)) return;

            let updatedValue = {};
            if (!R.isNil(index)) {
                updatedValue = [...currentValue.value];
                updatedValue[index][parameter.name] = settings;
            } else {
                updatedValue = { ...currentValue.value };
                updatedValue[parameter.name] = settings;
            }
            currentValue.value = updatedValue;

            const processedUpdatedValue = (props.complexParameterOutgoingProcessor as any)(
                R.isEmpty(updatedValue) ? defaultValue.value : updatedValue,
            );

            emit('change', processedUpdatedValue);

            forceUpdate.value = new Date();
        };

        const addAnother = () => {
            currentValue.value.push({});
            const processedUpdatedValue = (props.complexParameterOutgoingProcessor as any)(
                R.isEmpty(currentValue.value) ? defaultValue.value : currentValue.value,
            );
            emit('change', processedUpdatedValue);
            forceUpdate.value = new Date();
        };

        const deleteEntry = (index: number) => {
            currentValue.value.splice(index, 1);
            const processedUpdatedValue = (props.complexParameterOutgoingProcessor as any)(
                R.isEmpty(currentValue.value) ? defaultValue.value : currentValue.value,
            );
            emit('change', processedUpdatedValue);
        };

        const updateValue = (newValue: any) => {
            currentValue.value = findInitialValue(newValue);
            const processedUpdatedValue = (props.complexParameterOutgoingProcessor as any)(
                R.isEmpty(currentValue.value) ? defaultValue.value : currentValue.value,
            );
            if (JSON.stringify(newValue) !== JSON.stringify(processedUpdatedValue)) {
                emit('change', processedUpdatedValue);
            }
        };

        // Watch changes in the incoming value to adapt the currently held value
        watch(
            () => props.value,
            (newValue: any) => updateValue(newValue),
            { immediate: true },
        );

        watch(
            () => props.visible,
            (visible: any) => {
                if (!visible) {
                    emit('change', { value: null });
                    forceUpdate.value = new Date();
                }
            },
            { immediate: true },
        );

        return { currentValue, isArray, change, addAnother, deleteEntry, hasValue, forceUpdate, subParameters, R };
    },
});
